import { Fragment, useState, useEffect, useMemo, useRef } from 'react';
import { useIsMobile } from '../utils/useIsMobile';
import { Link, useSearchParams } from '../routing';
import SupplierSpendEmptyState from '../components/SupplierSpendEmptyState';
import InlineLoadError from '../components/InlineLoadError';
import { analysisApi, catalogApi, suppliersApi, classificationsApi, formatCurrency, fetchDocFileObjectUrl } from '../utils/api';
import { toast } from '../components/Toast';
import AnalysisFilterBar from '../components/AnalysisFilterBar';
import ListRow from '../components/ListRow';
import FilterSheet from '../components/FilterSheet';
import MobileSort from '../components/MobileSort';
import { useAuth } from '../context/AuthContext';
import { track, EVENTS } from '../utils/analytics';
import { useSortable } from '../utils/useSortable';
import { formatDateShort } from '../utils/dateFormat';
import { currentMonthRange, periodLabel, PRESETS_ANALYTIC, activePresetKey, readPeriodParams, withPeriodParams } from '../utils/period';
import { usePresetPeriod } from '../utils/usePresetPeriod';
import Icon from '../components/Icon';
import ExpandCaret from '../components/ExpandCaret';
import ExportMenu from '../components/ExportMenu';
import NoAccessNotice from '../components/NoAccessNotice';
import HqOnlyNotice, { HqOnlyLock } from '../components/HqOnlyNotice';
import PageViewAsMenu from '../components/ViewAsMenu';

// The full "רכש לפי ספק" report — the by-supplier workbench that used to live on
// /analysis. It moved here when that card became the donut summary
// (SupplierDonutCard); the donut's לצפייה ← footer is the door in, forwarding
// the active date range as ?from=&to=.
//
// This is the SINGLE-restaurant supplier view only — the network (chain)
// consolidated report, the budget panel and the by-classification card all stay on
// /analysis. Everything here is a straight relocation of that view, plus a
// breadcrumb, a URL-synced date range, and a two-option export.

// ▲/▼ change indicator (price UP is bad in procurement → red).
function renderChange(pct) {
  if (pct === null || pct === undefined) return <span className="change-zero">—</span>;
  const n = parseFloat(pct);
  if (n > 0) return <span className="change-pos"><Icon name="arrow-up" size={12} /> {n}%</span>;
  if (n < 0) return <span className="change-neg"><Icon name="arrow-down" size={12} /> {Math.abs(n)}%</span>;
  return <span className="change-zero">0%</span>;
}

// Mobile % delta chip — null when there's no history so quiet rows stay quiet.
function deltaChip(pct) {
  if (pct === null || pct === undefined) return null;
  const n = parseFloat(pct);
  if (Number.isNaN(n)) return null;
  const cls = n > 0 ? 'is-up' : n < 0 ? 'is-down' : 'is-zero';
  return <span className={`m-delta ${cls}`}>{n > 0 ? `+${n}%` : `${n}%`}</span>;
}

export default function SupplierPurchaseReportPage() {
  // Table vs cards switches with the filter bar and the view tabs, at the
  // filter-system breakpoint (720) — not at 640, which is the DENSITY step.
  const isMobile = useIsMobile();
  const { canViewDiscounts, canEdit, isAdmin } = useAuth();

  // Date range is URL-carried so a reload, a bookmark and the donut card's
  // forwarded link all land on the same period. Written with `replace: true`
  // ON PURPOSE: pushing a history entry per date change would make Back walk
  // the user's own filter edits one at a time instead of returning to
  // /analysis, so back/forward deliberately does NOT step between periods.
  const [searchParams, setSearchParams] = useSearchParams();
  // A preset is carried as `?period=<key>` and re-evaluated to today, so the
  // bookmark doesn't freeze the dates it produced (period.js, withPeriodParams).
  // AnalysisFilterBar offers PRESETS_ANALYTIC.
  const urlRange = readPeriodParams(searchParams, PRESETS_ANALYTIC);
  const [dateFrom, setDateFrom] = useState(urlRange.from || currentMonthRange().from);
  const [dateTo, setDateTo] = useState(urlRange.to || currentMonthRange().to);
  const setDateRange = (f, t) => {
    setDateFrom(f);
    setDateTo(t);
    // Called only with a range computed just now (the picker, or the roll
    // below), so matching it against today's presets is sound here.
    setSearchParams(withPeriodParams(searchParams, activePresetKey(f, t, PRESETS_ANALYTIC), f, t), { replace: true });
  };
  // Rolls a preset forward when the tab comes back after the day changed.
  usePresetPeriod(dateFrom, dateTo, PRESETS_ANALYTIC, setDateRange);

  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [accessDenied, setAccessDenied] = useState('');

  const [nameFilter, setNameFilter] = useState('');
  const [topProductFilter, setTopProductFilter] = useState('');
  const [productNameFilter, setProductNameFilter] = useState('');
  const [productSkuFilter, setProductSkuFilter] = useState('');
  const [editingIdx, setEditingIdx] = useState(null);
  const [editForm, setEditForm] = useState({ sku: '', last_price: '', fixed_price: '' });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productHistory, setProductHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [editingSupDisc, setEditingSupDisc] = useState(false);
  const [supDiscForm, setSupDiscForm] = useState({ fixed_invoice_discount: '', fixed_invoice_discount_type: 'percent' });

  const [classifications, setClassifications] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState([]); // catalog_id[]

  // Mobile product detail sheet + top-10 accordion cap.
  const [productSheet, setProductSheet] = useState(null);
  const [showAllProducts, setShowAllProducts] = useState(false);

  const loadClassifications = async () => {
    const r = await classificationsApi.getAll();
    setClassifications(r.data);
  };
  useEffect(() => { loadClassifications().catch(() => setClassifications([])); }, []);

  const buildParams = () => {
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return params;
  };

  const fetchSuppliers = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const r = await analysisApi.getAll(buildParams());
      setSuppliers(r.data);
    } catch {
      // A failed fetch must never fall through to the empty state — "אין נתונים"
      // on a spend screen reads as "you bought nothing", not "the load failed".
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const refreshAll = async () => {
    await fetchSuppliers();
    if (selectedSupplier) {
      setLoadingProducts(true);
      try {
        const r = await analysisApi.getSupplier(selectedSupplier.id, buildParams());
        setProducts(r.data);
      } catch (error) {
        if (error?.response?.data?.code === 'HQ_ONLY_SUPPLIER') {
          setAccessDenied(error.response.data.error);
        }
      } finally {
        setLoadingProducts(false);
      }
    }
  };

  useEffect(() => {
    setSelectedSupplier(null);
    setProducts([]);
    fetchSuppliers();
  }, [dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setSelectedProductIds([]); }, [selectedSupplier?.id, dateFrom, dateTo]);

  // Pull fresh data when the tab regains focus — the analysis SQL reads live
  // item prices, but this page's state is otherwise frozen until interaction.
  //
  // BOTH events are bound because neither alone covers every path back (a
  // window focus without a visibility change, and vice versa), but Chrome fires
  // visibilitychange AND focus on a normal tab re-selection, so the naive
  // version ran the refetch twice — two `GET /analysis` + two per-supplier
  // fetches, ~29ms of a SYNCHRONOUS better-sqlite3 connection blocked for a
  // duplicate answer, plus an uncancelled race between the two responses. The
  // timestamp guard collapses the pair into one refresh.
  const lastRefreshRef = useRef(0);
  useEffect(() => {
    const onFocus = () => {
      if (document.hidden) return;
      const now = Date.now();
      if (now - lastRefreshRef.current < 2000) return;
      lastRefreshRef.current = now;
      refreshAll();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [selectedSupplier, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedSupplier) return;
    const el = document.getElementById(`supplier-expansion-${selectedSupplier.id}`);
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedSupplier?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedProduct) return;
    const el = document.getElementById(`product-expansion-${selectedProduct.group_key}`);
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedProduct?.group_key]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSupplierClick = async (supplier) => {
    setAccessDenied('');
    setShowAllProducts(false);
    if (selectedSupplier?.id === supplier.id) {
      setSelectedSupplier(null);
      setProducts([]);
      setEditingIdx(null);
      setSelectedProduct(null);
      setProductHistory([]);
      setProductNameFilter('');
      setProductSkuFilter('');
      setEditingSupDisc(false);
      return;
    }
    setEditingIdx(null);
    setSelectedProduct(null);
    setProductHistory([]);
    setProductNameFilter('');
    setProductSkuFilter('');
    setEditingSupDisc(false);
    setSelectedSupplier(supplier);
    setLoadingProducts(true);
    try {
      const r = await analysisApi.getSupplier(supplier.id, buildParams());
      setProducts(r.data);
    } catch (error) {
      if (error?.response?.data?.code === 'HQ_ONLY_SUPPLIER') {
        setAccessDenied(error.response.data.error);
      } else {
        toast('שגיאה בטעינת מוצרי הספק', 'error');
      }
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleProductClick = async (p) => {
    if (selectedProduct?.group_key === p.group_key) {
      setSelectedProduct(null);
      setProductHistory([]);
      return;
    }
    setEditingIdx(null);
    setSelectedProduct(p);
    setProductHistory([]);
    setLoadingHistory(true);
    try {
      // Product history is intentionally NOT date-filtered — the user wants the
      // full purchase history regardless of the page-level date filter.
      const r = await analysisApi.getProduct(selectedSupplier.id, {
        sku: p.sku || '',
        desc: p.description,
      });
      setProductHistory(r.data);
    } catch {
      toast('שגיאה בטעינת היסטוריית מוצר', 'error');
    } finally {
      setLoadingHistory(false);
    }
  };

  const isReturnRow = (row) => ['חשבונית זיכוי', 'תעודת החזרה'].includes(row.doc_type);

  const historyNetPrice = (row) => {
    const price = row.unit_price || 0;
    const disc = row.discount || 0;
    if (row.discount_type === 'amount') return Math.round((price - disc) * 1000) / 1000;
    return Math.round(price * (1 - disc / 100) * 1000) / 1000;
  };

  const historyDiscLabel = (row) => {
    if (!row.discount) return '—';
    if (row.discount_type === 'amount') return formatCurrency(row.discount);
    return `${row.discount}%`;
  };

  const startEdit = (p, idx) => {
    setEditingIdx(idx);
    setEditForm({
      sku: p.sku || '',
      name: p.display_name || p.description || '',
      last_price: p.current_price || '',
      fixed_price: (p.fixed_price || 0) > 0 ? p.fixed_price : '',
      fixed_discount: (p.fixed_discount || 0) > 0 ? p.fixed_discount : '',
      fixed_discount_type: p.fixed_discount_type || 'percent',
    });
  };

  const cancelEdit = () => setEditingIdx(null);

  const saveEdit = async (p, idx) => {
    try {
      const newSku = (editForm.sku || '').trim();
      const oldSku = (p.sku || '').trim();
      const oldName = (p.display_name || p.description || '').trim();
      const newFixedPrice = parseFloat(editForm.fixed_price);
      const newFixedDiscount = parseFloat(editForm.fixed_discount);
      const newName = (editForm.name || '').trim() || p.description;
      const r = await catalogApi.updateItem(selectedSupplier.id, {
        name: newName,
        old_sku: oldSku,
        old_name: oldName,
        sku: newSku,
        last_price: parseFloat(editForm.last_price) || 0,
        fixed_price: isNaN(newFixedPrice) ? 0 : newFixedPrice,
        fixed_discount: isNaN(newFixedDiscount) ? 0 : newFixedDiscount,
        fixed_discount_type: editForm.fixed_discount_type || 'percent',
      });
      setEditingIdx(null);
      if (r?.data?.merged) {
        toast('המוצרים אוחדו');
        const refreshed = await analysisApi.getSupplier(selectedSupplier.id, buildParams());
        setProducts(refreshed.data);
      } else {
        setProducts(prev => prev.map((prod, i) =>
          i === idx ? {
            ...prod,
            sku: newSku,
            display_name: newName,
            current_price: parseFloat(editForm.last_price) || prod.current_price,
            fixed_price: isNaN(newFixedPrice) ? 0 : newFixedPrice,
            fixed_discount: isNaN(newFixedDiscount) ? 0 : newFixedDiscount,
            fixed_discount_type: editForm.fixed_discount_type || 'percent',
          } : prod
        ));
        toast('קטלוג עודכן');
      }
    } catch {
      toast('שגיאה בשמירה', 'error');
    }
  };

  const startSupDiscEdit = (s) => {
    setEditingSupDisc(true);
    setSupDiscForm({
      fixed_invoice_discount: (s.fixed_invoice_discount || 0) > 0 ? s.fixed_invoice_discount : '',
      fixed_invoice_discount_type: s.fixed_invoice_discount_type || 'percent',
    });
  };

  const saveSupDisc = async (s) => {
    try {
      const val = parseFloat(supDiscForm.fixed_invoice_discount);
      const body = {
        fixed_invoice_discount: isNaN(val) ? 0 : val,
        fixed_invoice_discount_type: supDiscForm.fixed_invoice_discount_type || 'percent',
      };
      await suppliersApi.setInvoiceDiscount(s.id, body);
      setSuppliers(prev => prev.map(sup => sup.id === s.id ? { ...sup, ...body } : sup));
      setSelectedSupplier(sup => sup && sup.id === s.id ? { ...sup, ...body } : sup);
      setEditingSupDisc(false);
      toast('הנחת הספק עודכנה');
    } catch {
      toast('שגיאה בשמירת הנחת הספק', 'error');
    }
  };

  const clearFilters = () => {
    const fresh = currentMonthRange();
    setNameFilter('');
    setTopProductFilter('');
    setDateRange(fresh.from, fresh.to);
  };

  const _topProductQ = topProductFilter.trim().toLowerCase();
  // Memoised for a stable array identity: this feeds the sort below and the
  // totals row, and rebuilding it on every render made both redo their work on
  // keystrokes that changed neither.
  const filtered = useMemo(() => suppliers.filter(s => {
    if (nameFilter && !s.name.includes(nameFilter)) return false;
    if (_topProductQ) {
      const hit = (s.products || []).some(p =>
        (p.display_name || '').toLowerCase().includes(_topProductQ) ||
        (p.readable_name || '').toLowerCase().includes(_topProductQ) ||
        (p.description || '').toLowerCase().includes(_topProductQ)
      );
      if (!hit) return false;
    }
    return true;
  }), [suppliers, nameFilter, _topProductQ]);

  const hasFilters = nameFilter || topProductFilter || dateFrom || dateTo;

  // ONE sort state for the desktop headers and the mobile sort sheet. They used
  // to be able to disagree — mobile sorted, desktop always rendered the server's
  // order — so the same list under the same filters told two different stories
  // depending on the viewport.
  //
  // Sorting is client-side on the whole filtered set, never a page of it, which
  // is what keeps the סה"כ row honest: a sort is a permutation, so the totals it
  // sums are the same totals either way. (Measured on the largest real tenant —
  // 130 supplier rows all-time — a sort costs 0.02ms; the render is the cost,
  // and it is the same render the filter box already pays for.)
  const {
    sorted: sortedSuppliers, sort: supSort, setSortTo: setSupSort,
    indicator: supInd, headerProps: supTh,
  } = useSortable(filtered, {
    name:   s => s.name,
    qty:    s => s.product_count || 0,
    total:  s => s.total_amount || 0,
    change: s => (s.change_pct === null || s.change_pct === undefined ? null : Math.abs(parseFloat(s.change_pct))),
  });

  const _productNameQ = productNameFilter.trim().toLowerCase();
  const _productSkuQ = productSkuFilter.trim().toLowerCase();
  const filteredProducts = products.filter(p => {
    if (_productNameQ && !(
      (p.description || '').toLowerCase().includes(_productNameQ) ||
      (p.display_name || '').toLowerCase().includes(_productNameQ)
    )) return false;
    if (_productSkuQ && !(p.sku || '').toLowerCase().includes(_productSkuQ)) return false;
    return true;
  });
  const hasProductFilters = _productNameQ || _productSkuQ;

  const taggableVisibleIds = filteredProducts.filter(p => p.catalog_id).map(p => p.catalog_id);
  const allVisibleSelected = taggableVisibleIds.length > 0 && taggableVisibleIds.every(id => selectedProductIds.includes(id));
  const someVisibleSelected = taggableVisibleIds.some(id => selectedProductIds.includes(id));

  const { sorted: sortedProducts, indicator: prodInd, headerProps: sortableTh } = useSortable(filteredProducts, {
    total: p => p.total_amount || 0,
    current: p => p.current_price || 0,
  });

  const productIndex = useMemo(() => {
    const m = new Map();
    products.forEach((p, i) => m.set(p, i));
    return m;
  }, [products]);

  if (accessDenied) return <NoAccessNotice message={accessDenied} />;

  return (
    <div>
      {/* The shared .breadcrumb class, like /item-classifications and the document
          screens. This was the one hand-styled `.breadcrumbs` (plural) in the
          app — a class with no CSS rule anywhere, so every value here was an
          inline override of nothing. Same words, same look, one implementation. */}
      <div className="breadcrumb">
        <Link className="link" to="/analysis">ניתוח רכש</Link>
        <Icon name="chevron-inline" size={13} className="breadcrumb-sep" />
        <span>רכש לפי ספק</span>
      </div>

      <div className="page-title-row">
        <div className="page-title-group">
          <h1 className="page-title">רכש לפי ספק</h1>
          <HqOnlyNotice amount={filtered.filter(s => s.hq_only).reduce((n, x) => n + (x.total_amount || 0), 0)} pending={loading} basis="רכש בתקופה (ללא מע״מ)" />
        </div>
        <div className="page-title-actions">
          <ExportMenu
            scopeLabel={periodLabel(dateFrom, dateTo)}
            options={[
              {
                key: 'suppliers',
                label: 'רכש לפי ספק',
                hint: 'שורה לכל ספק: סכום, מחיר נוכחי ואחוז שינוי',
                run: () => {
                  track(EVENTS.ANALYSIS_EXPORTED, { report_type: 'suppliers' });
                  return analysisApi.exportAll({
                    ...buildParams(),
                    ...(nameFilter || topProductFilter
                      ? { supplier_ids: filtered.map(s => s.id).join(',') } : {}),
                  });
                },
              },
              {
                key: 'items',
                label: 'רכש לפי פריטים',
                hint: 'שורה לכל שורת רכש, כל הספקים. קובץ גדול, ההכנה עשויה לקחת מספר שניות',
                run: () => {
                  track(EVENTS.ANALYSIS_EXPORTED, { report_type: 'all_items' });
                  return analysisApi.exportAllItems({
                    ...buildParams(),
                    ...(nameFilter || topProductFilter
                      ? { supplier_ids: filtered.map(s => s.id).join(',') } : {}),
                  });
                },
              },
            ]}
          />
          <PageViewAsMenu />
        </div>
      </div>

      <AnalysisFilterBar
        nameFilter={nameFilter}             setNameFilter={setNameFilter}
        topProductFilter={topProductFilter} setTopProductFilter={setTopProductFilter}
        dateFrom={dateFrom}                 dateTo={dateTo}
        setDateRange={setDateRange}
        hasFilters={Boolean(hasFilters)}
        onClearAll={clearFilters}
        resultCount={filtered.length}
        sortControl={
          // Same sort state as the desktop headers, so the two viewports can
          // never order the same list differently. Capped at four options per
          // the mobile-sort convention: כמות is left off because on a phone
          // "how much did I spend" outranks "how many lines".
          <MobileSort
            options={[
              { key: 'total',  label: 'סכום (ללא מע״מ)' },
              { key: 'change', label: 'שינוי מחיר' },
              { key: 'name',   label: 'שם ספק' },
            ]}
            sort={supSort}
            onChange={setSupSort}
          />
        }
      />

      <div className="card">
        {loading ? (
          <div className="loading-container"><div className="spinner" /><span>טוען...</span></div>
        ) : loadError ? (
          <InlineLoadError message="לא ניתן לטעון את נתוני הניתוח" onRetry={fetchSuppliers} />
        ) : filtered.length === 0 ? (
          // Shared with the donut card on /analysis so both screens tell a
          // filter miss, an empty period and a brand-new account apart in the
          // same words. `hasFilters` is NOT the signal here: it also counts the
          // period, which is always set, so it could never be false.
          <SupplierSpendEmptyState
            period={periodLabel(dateFrom, dateTo)}
            filterActive={Boolean(nameFilter || topProductFilter)}
          />
        ) : (
          <>
          {/* Mobile (≤640px): accordion list rows. Desktop workbench below. */}
          {isMobile && (
          <div>
            <div className="m-list">
              {sortedSuppliers.map(s => {
                const open = selectedSupplier?.id === s.id;
                return (
                  <Fragment key={s.id}>
                    <ListRow
                      variant="accordion"
                      expanded={open}
                      primary={<>{s.name}<HqOnlyLock hqOnly={s.hq_only} /></>}
                      meta={`${s.product_count} מוצרים`}
                      amount={formatCurrency(s.total_amount)}
                      endSub={deltaChip(s.change_pct)}
                      onClick={() => handleSupplierClick(s)}
                    />
                    {open && (
                      <div className="m-subrows">
                        {loadingProducts ? (
                          <div style={{ padding: 12, color: 'var(--ink-muted)', fontSize: 13 }}>טוען מוצרים...</div>
                        ) : sortedProducts.length === 0 ? (
                          <div style={{ padding: 12, color: 'var(--ink-subtle)', fontSize: 13 }}>אין מוצרים בתקופה שנבחרה</div>
                        ) : (() => {
                          const bykSpend = [...sortedProducts].sort(
                            (a, b) => (b.total_amount || 0) - (a.total_amount || 0));
                          const visible = showAllProducts ? bykSpend : bykSpend.slice(0, 10);
                          return (
                            <>
                              {visible.map(p => (
                                <div
                                  key={p.group_key}
                                  className="m-subrow"
                                  role="button"
                                  tabIndex={0}
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => setProductSheet(p)}
                                >
                                  <div className="m-subrow-main">
                                    <div className="m-subrow-primary">{p.display_name || p.description}</div>
                                    <div className="m-subrow-meta">
                                      {p.total_qty} יח׳ × {formatCurrency(p.current_price)}
                                      <span style={{ marginInlineStart: 6 }}>{deltaChip(p.change_pct)}</span>
                                    </div>
                                  </div>
                                  <span className="m-subrow-amount">{formatCurrency(p.total_amount)}</span>
                                </div>
                              ))}
                              {!showAllProducts && bykSpend.length > 10 && (
                                <button
                                  type="button"
                                  className="m-subrow-more"
                                  onClick={() => setShowAllProducts(true)}
                                >הצג הכל ({bykSpend.length})</button>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </Fragment>
                );
              })}
            </div>
          </div>
          )}

          {!isMobile && (
          <div className="table-wrapper table-scroll-mobile">
            <table>
              <thead>
                <tr>
                  {/* Sortable: the supplier and the three MEASURES. The price
                      columns stay unsorted on purpose — min/max/current are
                      per-product figures rolled up to the supplier, so ordering
                      suppliers by them would rank on a number that means
                      something different in each row. */}
                  <th {...supTh('name')}>ספק{supInd('name')}</th>
                  <th {...supTh('qty')}>כמות{supInd('qty')}</th>
                  <th {...supTh('total')}>סכום (ללא מע״מ){supInd('total')}</th>
                  <th>מחיר מינימום</th>
                  <th>מחיר מקסימום</th>
                  <th>מחיר נוכחי</th>
                  <th>% שינוי</th>
                </tr>
              </thead>
              <tbody>
                {sortedSuppliers.map(s => (
                  <Fragment key={s.id}>
                    <tr className="row-clickable" onClick={() => handleSupplierClick(s)}
                      style={{ background: selectedSupplier?.id === s.id ? 'var(--selected-soft-bg)' : undefined }}>
                      <td style={{ fontWeight: 600 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <ExpandCaret open={selectedSupplier?.id === s.id} />{s.name}<HqOnlyLock hqOnly={s.hq_only} />
                        </span>
                      </td>
                      <td>{s.product_count}</td>
                      <td>{formatCurrency(s.total_amount)}</td>
                      <td>{formatCurrency(s.min_price)}</td>
                      <td>{formatCurrency(s.max_price)}</td>
                      <td>{formatCurrency(s.current_price)}</td>
                      <td>{renderChange(s.change_pct)}</td>
                    </tr>
                    {selectedSupplier?.id === s.id && (
                      <tr id={`supplier-expansion-${s.id}`}>
                        <td colSpan={7} style={{ padding: 0, background: 'var(--tint)', borderTop: '1px solid var(--line)' }}>
                          <div style={{ padding: '16px 20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                              <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--ink)', margin: 0 }}>
                                מוצרי {s.name}
                                {(dateFrom || dateTo) && <span style={{ fontWeight: 400, color: 'var(--ink-muted)', marginRight: 8, fontSize: '0.88rem' }}>— {formatDateShort(dateFrom)}{dateTo && dateFrom !== dateTo ? ` עד ${formatDateShort(dateTo)}` : ''}</span>}
                              </h3>
                            </div>

                            {(() => {
                              const disc = canViewDiscounts ? (s.invoice_discount_total || 0) : 0;
                              const pdc = s.price_diff_credit_total || 0;
                              if (disc === 0 && pdc === 0) return null;
                              return (
                                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center',
                                  background: 'var(--success-bg)', border: '1px solid var(--success-line)', borderRadius: 8,
                                  padding: '10px 16px', marginBottom: 16 }}>
                                  <span>סכום מוצרים: <b>{formatCurrency(s.total_amount + disc + pdc)}</b></span>
                                  {pdc !== 0 && (
                                    <span style={{ color: 'var(--danger-ink)' }}>זיכוי הפרש מחיר: <b>{formatCurrency(-pdc)}</b></span>
                                  )}
                                  {disc !== 0 && (
                                    <span style={{ color: 'var(--danger-ink)' }}>הנחת חשבונית: <b>{formatCurrency(-disc)}</b></span>
                                  )}
                                  <span style={{ fontWeight: 700 }}>סכום כולל: {formatCurrency(s.total_amount)}</span>
                                </div>
                              );
                            })()}

                            {canViewDiscounts && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                                background: 'var(--warning-bg)', border: '1px solid var(--warning-line)', borderRadius: 8,
                                padding: '8px 14px', marginBottom: 16 }}>
                                <span style={{ fontWeight: 700, color: 'var(--warning-ink)' }}>הנחת חשבונית קבועה לספק:</span>
                                {editingSupDisc && isAdmin ? (
                                  <>
                                    <input type="number" step="any" min="0" autoFocus
                                      value={supDiscForm.fixed_invoice_discount}
                                      placeholder="—"
                                      onChange={e => setSupDiscForm(f => ({ ...f, fixed_invoice_discount: e.target.value }))}
                                      style={{ width: 90, border: '1px solid var(--warning-line)', borderRadius: 6, padding: '4px 8px', background: '#fff' }} />
                                    <select value={supDiscForm.fixed_invoice_discount_type}
                                      onChange={e => setSupDiscForm(f => ({ ...f, fixed_invoice_discount_type: e.target.value }))}
                                      style={{ border: '1px solid var(--warning-line)', borderRadius: 6, padding: '4px 8px', background: '#fff' }}>
                                      <option value="percent">%</option>
                                      <option value="amount">₪</option>
                                    </select>
                                    <button className="btn btn-sm btn-primary" onClick={() => saveSupDisc(s)}>שמור</button>
                                    <button className="btn btn-sm btn-secondary" onClick={() => setEditingSupDisc(false)}>ביטול</button>
                                  </>
                                ) : (
                                  <>
                                    <span style={{ fontWeight: 600, color: 'var(--warning-ink)' }}>
                                      {(s.fixed_invoice_discount || 0) > 0
                                        ? (s.fixed_invoice_discount_type === 'amount'
                                            ? formatCurrency(s.fixed_invoice_discount)
                                            : `${s.fixed_invoice_discount}%`)
                                        : 'לא הוגדרה'}
                                    </span>
                                    {isAdmin && (
                                      <button className="btn btn-sm btn-secondary" onClick={() => startSupDiscEdit(s)}>ערוך</button>
                                    )}
                                  </>
                                )}
                              </div>
                            )}

                            {!loadingProducts && products.length > 0 && (
                              <div className="filter-bar" style={{ marginBottom: 16 }}>
                                <div className="filter-group">
                                  <label>שם מוצר</label>
                                  <input
                                    className="filter-input"
                                    placeholder="חיפוש לפי שם מוצר..."
                                    value={productNameFilter}
                                    onChange={e => setProductNameFilter(e.target.value)}
                                    style={{ width: 220 }}
                                  />
                                </div>
                                <div className="filter-group">
                                  <label>מק"ט</label>
                                  <input
                                    className="filter-input"
                                    placeholder='חיפוש לפי מק"ט...'
                                    value={productSkuFilter}
                                    onChange={e => setProductSkuFilter(e.target.value)}
                                    style={{ width: 160 }}
                                  />
                                </div>
                                {hasProductFilters && (
                                  <button className="btn btn-secondary btn-sm"
                                    onClick={() => { setProductNameFilter(''); setProductSkuFilter(''); }}>
                                    <Icon name="close" size={14} /> נקה סינון מוצרים
                                  </button>
                                )}
                              </div>
                            )}

                            {!loadingProducts && products.length > 0 && (
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                                {selectedProductIds.length > 0 && (
                                  <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                    <span>{selectedProductIds.length} נבחרו</span>
                                    <select
                                      defaultValue=""
                                      onChange={async (e) => {
                                        if (e.target.value === '') return;
                                        const val = e.target.value === 'none' ? null : parseInt(e.target.value);
                                        e.target.value = '';
                                        try {
                                          await catalogApi.bulkSetClassification(selectedProductIds, val);
                                          track(EVENTS.CLASSIFICATION_TAGGED, { mode: 'bulk', product_count: selectedProductIds.length, cleared: val === null });
                                          setProducts(prev => prev.map(x => selectedProductIds.includes(x.catalog_id) ? { ...x, classification_id: val } : x));
                                          setSelectedProductIds([]);
                                          loadClassifications();
                                        } catch (err) {
                                          toast(err?.response?.data?.error || 'שגיאה בשיוך סיווגים', 'error');
                                        }
                                      }}
                                    >
                                      <option value="">שייך לסיווג…</option>
                                      <option value="none">— הסר סיווג —</option>
                                      {classifications.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                  </span>
                                )}
                              </div>
                            )}

                            {loadingProducts ? (
                              <div className="loading-container"><div className="spinner" /></div>
                            ) : products.length === 0 ? (
                              <div className="empty-state"><p>אין נתוני מחירים</p></div>
                            ) : filteredProducts.length === 0 ? (
                              <div className="empty-state"><p>לא נמצאו מוצרים לפי הסינון שנבחר</p></div>
                            ) : (
                              <div className="table-wrapper table-scroll-mobile nested-scroll-table">
                                <table>
                                  <thead>
                                    <tr>
                                      <th>
                                        <input
                                          type="checkbox"
                                          checked={allVisibleSelected}
                                          ref={el => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
                                          disabled={!canEdit || taggableVisibleIds.length === 0}
                                          onChange={e => {
                                            if (e.target.checked) {
                                              setSelectedProductIds(prev => Array.from(new Set([...prev, ...taggableVisibleIds])));
                                            } else {
                                              setSelectedProductIds(prev => prev.filter(id => !taggableVisibleIds.includes(id)));
                                            }
                                          }}
                                          title="סמן הכל"
                                        />
                                      </th>
                                      <th>מק"ט</th>
                                      <th>תיאור מוצר</th>
                                      <th>סיווג פריט</th>
                                      <th>כמות</th>
                                      <th {...sortableTh('total')}>סכום (ללא מע״מ){prodInd('total')}</th>
                                      <th>מחיר מינ'</th>
                                      <th>מחיר מקס'</th>
                                      <th>מחיר ממוצע</th>
                                      <th {...sortableTh('current')}>מחיר נוכחי{prodInd('current')}</th>
                                      <th>מחיר קודם</th>
                                      <th>% שינוי</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {sortedProducts.map((p) => {
                                      const i = productIndex.get(p);
                                      if (editingIdx === i) {
                                        return (
                                          <tr key={`edit-${i}`} style={{ background: 'var(--warning-bg)' }}>
                                            <td colSpan={10}>
                                              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                                                <label style={{ fontSize: '0.85rem', color: 'var(--ink-muted)' }}>שם:</label>
                                                <input
                                                  value={editForm.name}
                                                  onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                                                  placeholder="שם מוצר"
                                                  style={{ border: '1px solid var(--outline)', borderRadius: 6, padding: '4px 8px', minWidth: 220 }}
                                                />
                                                <label style={{ fontSize: '0.85rem', color: 'var(--ink-muted)' }}>מק"ט:</label>
                                                <input
                                                  value={editForm.sku}
                                                  onChange={e => setEditForm(f => ({ ...f, sku: e.target.value }))}
                                                  placeholder='מק"ט'
                                                  style={{ border: '1px solid var(--outline)', borderRadius: 6, padding: '4px 8px', width: 130 }}
                                                />
                                                <label style={{ fontSize: '0.85rem', color: 'var(--ink-muted)' }}>מחיר אחרון (₪):</label>
                                                <input
                                                  type="number"
                                                  value={editForm.last_price}
                                                  onChange={e => setEditForm(f => ({ ...f, last_price: e.target.value }))}
                                                  style={{ border: '1px solid var(--outline)', borderRadius: 6, padding: '4px 8px', width: 100 }}
                                                />
                                                <label
                                                  style={{ fontSize: '0.85rem', color: 'var(--warning-ink)', fontWeight: 600 }}
                                                  title="המחיר המוסכם מול הספק. כשיש מחיר קבוע — כל מסמך חדש במחיר שונה ייתפס כשינוי מחיר ויפתח אפשרות זיכוי על ההפרש. השאירי ריק כדי לבטל."
                                                >מחיר קבוע (₪):</label>
                                                <input
                                                  type="number"
                                                  value={editForm.fixed_price}
                                                  onChange={e => setEditForm(f => ({ ...f, fixed_price: e.target.value }))}
                                                  placeholder="—"
                                                  style={{ border: '1px solid var(--warning-line)', borderRadius: 6, padding: '4px 8px', width: 100, background: 'var(--warning-bg)' }}
                                                />
                                                <label
                                                  style={{ fontSize: '0.85rem', color: 'var(--warning-ink)', fontWeight: 600 }}
                                                  title="הנחה קבועה מוסכמת על הפריט הזה. כל מסמך חדש שמגיע ללא ההנחה (או עם הנחה שונה) יוצב כהפרה ויפתח אזהרה. השאירי ריק כדי לבטל."
                                                >הנחה קבועה:</label>
                                                <input
                                                  type="number"
                                                  value={editForm.fixed_discount}
                                                  onChange={e => setEditForm(f => ({ ...f, fixed_discount: e.target.value }))}
                                                  placeholder="—"
                                                  style={{ border: '1px solid var(--warning-line)', borderRadius: 6, padding: '4px 8px', width: 80, background: 'var(--warning-bg)' }}
                                                />
                                                <select
                                                  value={editForm.fixed_discount_type}
                                                  onChange={e => setEditForm(f => ({ ...f, fixed_discount_type: e.target.value }))}
                                                  style={{ border: '1px solid var(--warning-line)', borderRadius: 6, padding: '4px 8px', background: 'var(--warning-bg)' }}
                                                >
                                                  <option value="percent">%</option>
                                                  <option value="amount">₪</option>
                                                </select>
                                                <button className="btn btn-sm btn-primary" onClick={() => saveEdit(p, i)}>שמור</button>
                                                <button className="btn btn-sm btn-secondary" onClick={cancelEdit}>ביטול</button>
                                              </div>
                                            </td>
                                            <td colSpan={2}></td>
                                          </tr>
                                        );
                                      }
                                      const isOpen = selectedProduct?.group_key === p.group_key;
                                      return (
                                        <Fragment key={i}>
                                          <tr
                                            className="row-clickable"
                                            style={{ background: isOpen ? 'var(--selected-soft-bg)' : undefined }}
                                            onClick={() => handleProductClick(p)}
                                          >
                                            <td onClick={e => e.stopPropagation()}>
                                              <input type="checkbox"
                                                disabled={!canEdit || !p.catalog_id}
                                                checked={selectedProductIds.includes(p.catalog_id)}
                                                onChange={e => setSelectedProductIds(prev =>
                                                  e.target.checked ? [...prev, p.catalog_id] : prev.filter(id => id !== p.catalog_id))}
                                              />
                                            </td>
                                            <td style={{ color: 'var(--ink-muted)', fontSize: '0.85rem' }}>{p.sku || '—'}</td>
                                            <td style={{ fontWeight: 500 }}>
                                              <span style={{ marginLeft: 4, display: 'inline-flex' }}><ExpandCaret open={isOpen} size={12} /></span>
                                              {p.display_name || p.description}
                                              {(p.fixed_price || 0) > 0 && (
                                                <span
                                                  title={`מחיר קבוע: ${formatCurrency(p.fixed_price)}`}
                                                  style={{ marginRight: 6, fontSize: '0.72rem', color: 'var(--warning-ink)', background: 'var(--warning-bg)', border: '1px solid var(--warning-line)', borderRadius: 4, padding: '1px 6px', fontWeight: 600, whiteSpace: 'nowrap' }}
                                                >
                                                  מחיר קבוע {formatCurrency(p.fixed_price)}
                                                </span>
                                              )}
                                              {canViewDiscounts && (p.fixed_discount || 0) > 0 && (
                                                <span
                                                  title="הנחה קבועה מוסכמת על הפריט"
                                                  style={{ marginRight: 6, fontSize: '0.72rem', color: 'var(--warning-ink)', background: 'var(--warning-bg)', border: '1px solid var(--warning-line)', borderRadius: 4, padding: '1px 6px', fontWeight: 600, whiteSpace: 'nowrap' }}
                                                >
                                                  הנחה קבועה {p.fixed_discount_type === 'amount' ? formatCurrency(p.fixed_discount) : `${p.fixed_discount}%`}
                                                </span>
                                              )}
                                            </td>
                                            <td onClick={e => e.stopPropagation()}>
                                              <select
                                                value={p.classification_id ?? ''}
                                                onChange={async (e) => {
                                                  const val = e.target.value === '' ? null : parseInt(e.target.value);
                                                  try {
                                                    await catalogApi.setItemClassification(p.catalog_id, val);
                                                    track(EVENTS.CLASSIFICATION_TAGGED, { mode: 'single', product_count: 1, cleared: val === null });
                                                    setProducts(prev => prev.map(x => x.catalog_id === p.catalog_id ? { ...x, classification_id: val } : x));
                                                    loadClassifications();
                                                  } catch (err) {
                                                    toast(err?.response?.data?.error || 'שגיאה בשמירת סיווג', 'error');
                                                  }
                                                }}
                                                disabled={!canEdit || !p.catalog_id}
                                              >
                                                <option value="">— ללא —</option>
                                                {classifications.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                              </select>
                                            </td>
                                            <td>
                                              {p.total_qty}
                                              {(p.returned_qty || 0) > 0 && (
                                                <span
                                                  title={`הוחזרו ${p.returned_qty} יח' (הכמות מוצגת נטו)`}
                                                  style={{ marginRight: 6, fontSize: '0.72rem', color: 'var(--danger-ink)', background: 'var(--danger-bg)', border: '1px solid var(--danger-line)', borderRadius: 4, padding: '1px 6px', fontWeight: 600, whiteSpace: 'nowrap' }}
                                                >
                                                  הוחזר {p.returned_qty}
                                                </span>
                                              )}
                                            </td>
                                            <td>{formatCurrency(p.total_amount)}</td>
                                            <td>{formatCurrency(p.min_price)}</td>
                                            <td>{formatCurrency(p.max_price)}</td>
                                            <td>{formatCurrency(p.avg_price)}</td>
                                            <td style={{ fontWeight: 600 }}>{formatCurrency(p.current_price)}</td>
                                            <td style={{ color: 'var(--ink-muted)' }}>{p.prev_price ? formatCurrency(p.prev_price) : '—'}</td>
                                            <td>
                                              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                                {renderChange(p.change_pct)}
                                                {isAdmin && (
                                                  <button
                                                    className="btn btn-sm btn-secondary"
                                                    style={{ marginRight: 4 }}
                                                    onClick={e => { e.stopPropagation(); startEdit(p, i); }}
                                                  >ערוך קטלוג</button>
                                                )}
                                              </div>
                                            </td>
                                          </tr>
                                          {isOpen && (
                                            <tr id={`product-expansion-${p.group_key}`}>
                                              <td colSpan={12} style={{ padding: 0, background: 'var(--tint-strong)', borderTop: '1px solid var(--outline)' }}>
                                                <div style={{ padding: '16px 20px' }}>
                                                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                                                    <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--ink)', margin: 0, flex: 1 }}>
                                                      היסטוריית הזמנות — {p.display_name || p.description}
                                                      {p.sku && <span style={{ fontWeight: 400, color: 'var(--ink-muted)', marginRight: 8, fontSize: '0.88rem' }}>מק"ט: {p.sku}</span>}
                                                    </h3>
                                                    {!loadingHistory && productHistory.length > 0 && (
                                                      <button className="btn btn-outline btn-sm" onClick={() => analysisApi.exportProduct(s.id, { sku: p.sku || '', desc: p.description })}>
                                                        <Icon name="spreadsheet" size={15} /> ייצוא היסטוריה
                                                      </button>
                                                    )}
                                                  </div>

                                                  {loadingHistory ? (
                                                    <div className="loading-container"><div className="spinner" /></div>
                                                  ) : productHistory.length === 0 ? (
                                                    <div className="empty-state"><p>אין היסטוריית הזמנות</p></div>
                                                  ) : (
                                                    <div className="table-wrapper table-scroll-mobile">
                                                      <table>
                                                        <thead>
                                                          <tr>
                                                            <th>תאריך</th>
                                                            <th>מספר מסמך</th>
                                                            <th>סוג מסמך</th>
                                                            <th>כמות</th>
                                                            <th>מחיר ליחידה</th>
                                                            <th>הנחה</th>
                                                            <th>מחיר נטו</th>
                                                            <th>סך הכל</th>
                                                            <th></th>
                                                          </tr>
                                                        </thead>
                                                        <tbody>
                                                          {productHistory.map((row, hi) => (
                                                            <tr key={hi}>
                                                              <td>{formatDateShort(row.date) || '—'}</td>
                                                              <td style={{ color: 'var(--ink-muted)' }}>{row.doc_num || '—'}</td>
                                                              <td><span className={`badge ${isReturnRow(row) ? 'badge-red' : 'badge-blue'}`}>{row.doc_type}</span></td>
                                                              <td style={isReturnRow(row) ? { color: 'var(--danger-ink)' } : undefined}>{isReturnRow(row) ? -(row.qty || 0) : row.qty}</td>
                                                              <td>{formatCurrency(row.unit_price)}</td>
                                                              <td>{historyDiscLabel(row)}</td>
                                                              <td>{formatCurrency(historyNetPrice(row))}</td>
                                                              <td style={{ fontWeight: 600, ...(isReturnRow(row) ? { color: 'var(--danger-ink)' } : {}) }}>{formatCurrency(isReturnRow(row) ? -(row.line_total || 0) : row.line_total)}</td>
                                                              <td>
                                                                <button
                                                                  className="btn btn-sm btn-secondary"
                                                                  disabled={!row.file_path}
                                                                  onClick={async () => {
                                                                    if (!row.file_path) return;
                                                                    try {
                                                                      const url = await fetchDocFileObjectUrl(row.file_path);
                                                                      window.open(url, '_blank', 'noreferrer');
                                                                    } catch (err) {
                                                                      console.error('[view-doc]', err);
                                                                      toast('שגיאה בטעינת המסמך', 'error');
                                                                    }
                                                                  }}
                                                                >
                                                                  צפה במסמך
                                                                </button>
                                                              </td>
                                                            </tr>
                                                          ))}
                                                        </tbody>
                                                        <tfoot>
                                                          <tr style={{ background: 'var(--tint-strong)', fontWeight: 700, borderTop: '2px solid var(--outline)' }}>
                                                            <td colSpan={3} style={{ padding: '10px 16px', color: 'var(--ink)' }}>סה"כ</td>
                                                            <td style={{ padding: '10px 16px' }}>{productHistory.reduce((sum, r) => sum + (isReturnRow(r) ? -(r.qty || 0) : (r.qty || 0)), 0)}</td>
                                                            <td colSpan={3}></td>
                                                            <td style={{ padding: '10px 16px' }}>{formatCurrency(productHistory.reduce((sum, r) => sum + (isReturnRow(r) ? -(r.line_total || 0) : (r.line_total || 0)), 0))}</td>
                                                            <td></td>
                                                          </tr>
                                                        </tfoot>
                                                      </table>
                                                    </div>
                                                  )}
                                                </div>
                                              </td>
                                            </tr>
                                          )}
                                        </Fragment>
                                      );
                                    })}
                                  </tbody>
                                  <tfoot>
                                    <tr style={{ background: 'var(--tint-strong)', fontWeight: 700, borderTop: '2px solid var(--outline)' }}>
                                      <td style={{ padding: '10px 16px', color: 'var(--ink)' }} colSpan={4}>סה"כ</td>
                                      <td style={{ padding: '10px 16px' }}>{filteredProducts.reduce((sum, p) => sum + (p.total_qty || 0), 0)}</td>
                                      <td style={{ padding: '10px 16px' }}>{formatCurrency(filteredProducts.reduce((sum, p) => sum + (p.total_amount || 0), 0))}</td>
                                      <td colSpan={6}></td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: 'var(--tint-strong)', fontWeight: 700, borderTop: '2px solid var(--outline)' }}>
                  <td style={{ padding: '10px 16px', color: 'var(--ink)' }}>סה"כ</td>
                  <td style={{ padding: '10px 16px' }}>{filtered.reduce((s, x) => s + (x.product_count || 0), 0)}</td>
                  <td style={{ padding: '10px 16px' }}>{formatCurrency(filtered.reduce((s, x) => s + (x.total_amount || 0), 0))}</td>
                  <td colSpan={4}></td>
                </tr>
              </tfoot>
            </table>
          </div>
          )}
          </>
        )}
      </div>

      {/* Mobile product detail sheet — full price breakdown for the tapped row. */}
      {productSheet && (() => {
        const p = productSheet;
        return (
          <FilterSheet open onClose={() => setProductSheet(null)} title={p.display_name || p.description}>
            <div>
              {p.sku && <div className="sheet-kv"><label>מק"ט</label><span>{p.sku}</span></div>}
              <div className="sheet-kv"><label>כמות</label><span>{p.total_qty}</span></div>
              <div className="sheet-kv"><label>סכום (ללא מע״מ)</label><span>{formatCurrency(p.total_amount)}</span></div>
              <div className="sheet-kv">
                <label>מחיר נוכחי</label>
                <span style={{ fontWeight: 700, color: 'var(--figure)' }}>{formatCurrency(p.current_price)}</span>
              </div>
              <div className="sheet-kv"><label>מחיר קודם</label><span>{p.prev_price ? formatCurrency(p.prev_price) : '—'}</span></div>
              <div className="sheet-kv"><label>מחיר ממוצע</label><span>{formatCurrency(p.avg_price)}</span></div>
              <div className="sheet-kv"><label>מינימום / מקסימום</label>
                <span>{formatCurrency(p.min_price)} – {formatCurrency(p.max_price)}</span>
              </div>
              {(p.fixed_price || 0) > 0 && (
                <div className="sheet-kv">
                  <label>מחיר קבוע</label>
                  <span style={{ color: 'var(--warning-ink)', fontWeight: 600 }}>{formatCurrency(p.fixed_price)}</span>
                </div>
              )}
              <div className="sheet-kv"><label>% שינוי</label><span>{deltaChip(p.change_pct) || '—'}</span></div>
            </div>
          </FilterSheet>
        );
      })()}
    </div>
  );
}
